import React, { useEffect, useState } from 'react';
import { Stage, Layer, Rect } from 'react-konva';
import { Box, Button, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { Get, Post } from '../../../actions';
import { useTableHistory } from './hooks/useTableHistory';
import { GridLayer } from './konva/GridLayer';
import { TableGroup } from './konva/TableGroup';
import { AddNewTable } from './component/addNewTableDialog';
import {
  getDim,
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  toNorm,
} from './canvasConstants';
import { getTranslation } from "../../../heplers/translationHelper";

const SKELETON_POSITIONS = [
  { x: 80,  y: 80,  w: 130, h: 60 },
  { x: 280, y: 80,  w: 70,  h: 70 },
  { x: 430, y: 80,  w: 90,  h: 60 },
  { x: 80,  y: 220, w: 60,  h: 130 },
  { x: 220, y: 220, w: 130, h: 60 },
];

const normalize = (t) => ({
  ...t,
  positionX: toNorm(t.positionX, CANVAS_WIDTH),
  positionY: toNorm(t.positionY, CANVAS_HEIGHT),
});

const hasOverlap = (tables, id, newX, newY, shape, persons) => {
  const mDim = getDim(shape, persons);
  const mL = newX * CANVAS_WIDTH  - mDim.w / 2;
  const mR = mL + mDim.w;
  const mT = newY * CANVAS_HEIGHT - mDim.h / 2;
  const mB = mT + mDim.h;

  return tables.some((t) => {
    if (String(t.id) === String(id)) return false;
    const tDim = getDim(t.shape, t.persons);
    const tL = t.positionX * CANVAS_WIDTH  - tDim.w / 2;
    const tR = tL + tDim.w;
    const tT = t.positionY * CANVAS_HEIGHT - tDim.h / 2;
    const tB = tT + tDim.h;
    return !(mR <= tL || tR <= mL || mB <= tT || tB <= mT);
  });
};

const FloorPlanCanvas = ({ selectedArea, dayTime, enableMove }) => {
  const { tables, loading, canUndo, canRedo, dispatch } = useTableHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [clickPos,   setClickPos  ] = useState(null);
  const [editTable,  setEditTable ] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (!selectedArea?.id) return;
    dispatch({ type: 'SET_LOADING', value: true });
    Get(
      null,
      `TableManagement/GetAllTables?areaId=${selectedArea.id}&dayTime=${dayTime.dayTime}`,
      null,
      (res) => {
        dispatch({ type: 'LOAD_TABLES', tables: res.data.map(normalize) });
      },
      () => dispatch({ type: 'SET_LOADING', value: false })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArea, dayTime.dayTime]);

  const handleDragEnd = (id, newX, newY) => {
    const table = tables.find((t) => String(t.id) === String(id));
    if (!table) return;

    if (hasOverlap(tables, id, newX, newY, table.shape, table.persons)) {
      dispatch({ type: 'UNDO' });
      enqueueSnackbar(getTranslation("Table overlaps another — move cancelled"), { variant: 'warning' });
      return;
    }

    dispatch({ type: 'MOVE_TABLE', id, x: newX, y: newY });

    Post(
      { ...table, positionX: newX, positionY: newY, areaId: selectedArea.id, dayTime: dayTime.dayTime },
      'TableManagement/UpsertTable',
      null,
      () => {},
      () => {
        dispatch({ type: 'UNDO' });
        enqueueSnackbar(getTranslation("Failed to save position"), { variant: 'error' });
      }
    );
  };

  const handleStageClick = (e) => {
    if (!enableMove) return;
    if (e.target !== e.target.getStage()) return;
    const pos = e.target.getStage().getPointerPosition();
    setClickPos({ x: pos.x / CANVAS_WIDTH, y: pos.y / CANVAS_HEIGHT });
    setEditTable(null);
    setOpenDialog(true);
  };

  const reloadTables = () => {
    if (!selectedArea?.id) return;
    Get(
      null,
      `TableManagement/GetAllTables?areaId=${selectedArea.id}&dayTime=${dayTime.dayTime}`,
      null,
      (res) => dispatch({ type: 'LOAD_TABLES', tables: res.data.map(normalize) }),
      () => {}
    );
  };

  const handleCreate = (values) => {
    const payload = editTable
      ? { ...values, areaId: selectedArea.id, dayTime: dayTime.dayTime }
      : {
          ...values,
          positionX: clickPos?.x ?? 0.5,
          positionY: clickPos?.y ?? 0.5,
          areaId: selectedArea.id,
          dayTime: dayTime.dayTime,
          quantity: 1,
        };

    Post(
      payload,
      'TableManagement/UpsertTable',
      null,
      () => {
        reloadTables();
        setOpenDialog(false);
        setEditTable(null);
      },
      () => enqueueSnackbar(getTranslation("Failed to save table"), { variant: 'error' })
    );
  };

  const handleDelete = (table) => {
    Post(
      { id: table.id },
      'TableManagement/DeleteTable',
      null,
      () => {
        dispatch({ type: 'LOAD_TABLES', tables: tables.filter((t) => t.id !== table.id) });
        enqueueSnackbar(getTranslation("Table deleted"), { variant: 'success' });
      },
      () => enqueueSnackbar(getTranslation("Failed to delete table"), { variant: 'error' })
    );
  };

  return (
    <Box>
      {enableMove && (
        <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
          <Button size="small" variant="outlined" disabled={!canUndo}
            onClick={() => dispatch({ type: 'UNDO' })}>
            {getTranslation("Undo")}
          </Button>
          <Button size="small" variant="outlined" disabled={!canRedo}
            onClick={() => dispatch({ type: 'REDO' })}>
            {getTranslation("Redo")}
          </Button>
          <Typography variant="caption" style={{ color: '#999', marginLeft: 8 }}>
            {getTranslation("Click canvas to add a table · Click table to edit · Drag to reposition · Ctrl+Z / Ctrl+Y")}
          </Typography>
        </Box>
      )}

      <Box boxShadow={6} borderRadius={4} style={{ overflow: 'hidden', display: 'inline-block' }}>
        <Stage width={CANVAS_WIDTH} height={CANVAS_HEIGHT} onClick={handleStageClick}>
          <GridLayer />
          <Layer>
            {loading && SKELETON_POSITIONS.map((s, i) => (
              <Rect key={i} x={s.x} y={s.y} width={s.w} height={s.h}
                fill="#e0e0e0" cornerRadius={4} opacity={0.7} />
            ))}
            {!loading && tables.map((table) => (
              <TableGroup
                key={table.id}
                table={table}
                enableDrag={enableMove}
                onDragEnd={handleDragEnd}
                onHover={null}
                onClick={() => {
                  if (!enableMove) return;
                  setEditTable(table);
                  setOpenDialog(true);
                }}
              />
            ))}
          </Layer>
        </Stage>
      </Box>

      <AddNewTable
        open={openDialog}
        onClose={() => { setOpenDialog(false); setEditTable(null); }}
        onSubmit={handleCreate}
        onDelete={(table) => { handleDelete(table); setEditTable(null); setOpenDialog(false); }}
        editTable={editTable}
      />
    </Box>
  );
};

export default FloorPlanCanvas;
